import React, { useState } from 'react';
import { 
  Link, 
  Stack, 
  Box, 
  useMediaQuery, 
  useTheme,
  Menu,
  MenuItem,
  Button,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Cancel,
  ContactMail,
  PrivacyTip,
  LocalShipping,
  Gavel,
  Policy,
  ExpandMore,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const PolicyLinks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const links = [
    { to: '/Cancellation-and-Refund', label: 'Cancellation', icon: Cancel },
    { to: '/Contact-Us', label: 'Contact', icon: ContactMail },
    { to: '/Privacy-policy', label: 'Privacy', icon: PrivacyTip },
    { to: '/Shipping-and-delivery', label: 'Shipping', icon: LocalShipping },
    { to: '/Terms-and-conditions', label: 'Terms', icon: Gavel },
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Mobile view with dropdown menu
  if (isMobile) {
    return (
      <Box sx={{ textAlign: 'center', py: 1.5 }}>
        <Button
          onClick={handleClick}
          variant="text"
          size="small"
          endIcon={<ExpandMore />}
          startIcon={<Policy />}
          sx={{
            color: 'gray',
            fontSize: '0.75rem',
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Policies & Legal
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 180,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            },
          }}
          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        >
          {links.map((link, index) => (
            <MenuItem
              key={link.to}
              component={RouterLink}
              to={link.to}
              onClick={handleClose}
              sx={{
                fontSize: '0.8rem',
                py: 1,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <link.icon fontSize="small" sx={{ color: 'gray' }} />
              </ListItemIcon>
              <ListItemText primary={link.label} />
            </MenuItem>
          ))}
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ px: 2, py: 1, textAlign: 'center' }}>
            <Box
              component="span"
              sx={{
                fontSize: '0.6rem',
                color: 'rgba(0, 0, 0, 0.4)',
              }}
            >
              © {new Date().getFullYear()} AediaX
            </Box>
          </Box>
        </Menu>
      </Box>
    );
  }

  // Desktop/Tablet view with horizontal links
  return (
    <Box
      sx={{
        width: '100%',
        py: { sm: 1.5, md: 2 },
        px: { sm: 1, md: 2 },
      }}
    >
      <Stack
        direction="row"
        spacing={{ sm: 1, md: 2, lg: 3 }}
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        sx={{
          width: '100%',
        }}
      >
        {links.map((link, index) => (
          <React.Fragment key={link.to}>
            <Link
              component={RouterLink}
              to={link.to}
              color="inherit"
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: {
                  sm: '0.7rem',
                  md: '0.75rem',
                  lg: '0.8rem',
                  xl: '0.85rem',
                },
                color: 'gray',
                padding: { sm: '3px 8px', md: '4px 10px', lg: '5px 12px' },
                borderRadius: 1,
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  color: theme.palette.primary.main,
                },
                '& .MuiSvgIcon-root': {
                  fontSize: {
                    sm: '0.9rem',
                    md: '1rem',
                    lg: '1.1rem',
                    xl: '1.2rem',
                  },
                  mr: { sm: 0.3, md: 0.5 },
                },
              }}
            >
              <link.icon fontSize="inherit" />
              {link.label}
            </Link>
            {index < links.length - 1 && (
              <Box
                sx={{
                  width: '1px',
                  height: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  display: { xs: 'none', sm: index > 2 ? 'none' : 'block' },
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};

export default PolicyLinks;